import type { BreakEvenSide, Fixture } from '../api'
import { marketLabel } from '../markets'
import { formatTime, relativeTime } from '../time'
import { TeamBadge } from './TeamBadge'
import { Detail } from './Detail'
import { Term } from './Term'
import { WhyPanel } from './WhyPanel'

/**
 * How a threshold price was arrived at, with this fixture's own numbers in it.
 *
 * Three steps, and the reader can check each one: the break-even price is one divided by our
 * probability; taking exactly that is a zero-profit bet; so we ask for three points more before
 * calling anything worth backing.
 *
 * The series paragraph does not re-explain either idea. It exists to show the one thing the map
 * paragraph cannot — that the same edge is worth more over a series, because the stronger side gets
 * up to three goes at proving it. Omitted entirely on a Bo1, where there is no second question.
 */
function BackingThreshold({
  teamA,
  mapProbability,
  seriesProbability,
  minimumEdge,
  map,
  series,
}: {
  teamA: string
  mapProbability: number | null
  seriesProbability: number | null
  minimumEdge: number
  map: BreakEvenSide
  series: BreakEvenSide | null
}) {
  const odds = (value: number | null) => (value === null ? '\u2014' : value.toFixed(2))
  const edgePct = (minimumEdge * 100).toFixed(0)

  /*
   * Enough digits that the reader can do the sum and get our answer.
   *
   * A map probability is stored to two places and reproduces at two. A series probability is
   * DERIVED \u2014 58% a map is 61.8976% a Bo3 \u2014 so printing the card's rounded 62% inside the
   * division gave 1 \u00f7 (0.62 \u2212 0.03) = 1.69 against the 1.70 printed beside it as the answer.
   *
   * That is the whole failure this panel exists to avoid. A page that publishes its arithmetic so a
   * reader can disagree with it cannot show a sum that does not come out, and "near enough" is not
   * available when the reader is being invited to check.
   */
  const shown = (value: number) => {
    for (const places of [2, 3, 4]) {
      const rounded = Number(value.toFixed(places))
      // The test is whether the reader's sum PRINTS the same as ours, not whether it is close.
      // A gap of 0.003 is small and still shows 1.69 beside an answer of 1.70.
      if (
        (1 / (rounded - minimumEdge)).toFixed(2) === (1 / (value - minimumEdge)).toFixed(2)
      ) {
        return { decimal: value.toFixed(places), percent: `${(rounded * 100).toFixed(places - 2)}%` }
      }
    }
    return { decimal: value.toFixed(4), percent: `${(value * 100).toFixed(2)}%` }
  }

  return (
    <Detail label="Worth backing at — how these prices are worked out" heading={<span>Worth backing at</span>}>
      {mapProbability === null ? (
        <>These are the prices a bet would have to beat. There is no estimate behind them yet.</>
      ) : (
        <>
          <span className="block">
            A {shown(mapProbability).percent} map prediction for{' '}
            <strong className="text-ink">{teamA}</strong> means the break-even price for a bet is 1 ÷{' '}
            {shown(mapProbability).decimal} ={' '}
            <strong className="text-ink">{odds(map.fairOdds)}</strong>. Even odds are not worth
            taking — you want a meaningful edge — which is why we ask for {edgePct}% more: 1 ÷ ({' '}
            {shown(mapProbability).decimal} − {minimumEdge.toFixed(2)}) ={' '}
            <strong className="text-ink">{odds(map.valueOdds)}</strong>, the price under Map.
          </span>
          {series !== null && seriesProbability !== null ? (
            <span className="mt-2 block">
              Over the series that same edge compounds — the better side gets more than one go at
              proving it — so {shown(mapProbability).percent} a map becomes{' '}
              {shown(seriesProbability).percent} the series. The sum is the same: 1 ÷ ({' '}
              {shown(seriesProbability).decimal} − {minimumEdge.toFixed(2)}) ={' '}
              <strong className="text-ink">{odds(series.valueOdds)}</strong>, the price under
              Series. A shorter price, for a bet that is likelier to land.
            </span>
          ) : null}
        </>
      )}
    </Detail>
  )
}

/**
 * A match that has not started, with the price it would need to be worth backing.
 *
 * This is the only card on the site a reader can still act on, and it is deliberately shaped around
 * a decision rather than a forecast. "62% Team Falcons" leaves the reader to work out whether the
 * 1.55 they are being offered is any good; **"1.79 or better"** answers that directly.
 *
 * Two prices, per side, because a Bo3 and a single map are separately priced markets and quoting a
 * map threshold against a series price is a real way to lose money confidently.
 *
 * The roster caveat is not decoration. The schedule names teams; the model needs five account ids;
 * and the bridge between them is a guess until the lobby is visible. A stand-in makes a team
 * measurably weaker than its rating implies and every pre-match source is blind to it, so the
 * strength of that assumption is shown rather than folded silently into the number.
 */
export function FixtureCard({ fixture, showTournament = false }: {
  fixture: Fixture
  showTournament?: boolean
}) {
  const { breakEven } = fixture

  // Once the heroes are locked that is the number to act on, so it is the one shown. The pre-draft
  // figure stays visible beside it rather than being replaced: the bookmaker's price was set before
  // anyone could see the draft and has not moved since, so the gap between the two is the point.
  const drafted = fixture.draftSeriesProbabilityTeamA !== null
  const shownTeamA = fixture.draftSeriesProbabilityTeamA ?? fixture.seriesProbabilityTeamA

  const favouredA = (shownTeamA ?? 0.5) >= 0.5
  const favourite = favouredA ? fixture.teamAName : fixture.teamBName
  const chance = favouredA ? shownTeamA : 1 - (shownTeamA ?? 0)

  // Both scales, for team A. The series number is what the headline bar shows; the map number is
  // what the model actually estimates and what the MAP thresholds below are computed from.
  const mapForA = fixture.draftProbabilityTeamA ?? fixture.mapProbabilityTeamA
  const seriesForA = shownTeamA ?? 0

  const before = fixture.seriesProbabilityTeamA
  const shift =
    drafted && before !== null && fixture.draftSeriesProbabilityTeamA !== null
      ? (fixture.draftSeriesProbabilityTeamA - before) * 100
      : null

  return (
    <article className="rounded-lg border border-line bg-surface transition-colors hover:border-accent-edge">
      <header className="flex items-start justify-between gap-3 border-b border-line p-4 pb-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
            <span
              className={`flex items-center gap-1.5 ${favouredA ? 'font-semibold text-ink' : 'text-ink-soft'}`}
            >
              <TeamBadge name={fixture.teamAName} logo={fixture.teamALogo} />
              {fixture.teamAName}
            </span>
            <span className="text-xs text-ink-faint">vs</span>
            <span
              className={`flex items-center gap-1.5 ${!favouredA ? 'font-semibold text-ink' : 'text-ink-soft'}`}
            >
              <TeamBadge name={fixture.teamBName} logo={fixture.teamBLogo} />
              {fixture.teamBName}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-ink-faint">
            <span className="tabular">{formatTime(fixture.startTime)}</span>
            {showTournament && fixture.tournament ? (
              <span className="truncate">{fixture.tournament.replace(/\//g, ' · ')}</span>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="rounded bg-surface-raised px-1.5 py-0.5 text-xs text-ink-faint">
            {fixture.seriesLabel}
          </span>
          <span className="text-xs text-ink-faint">{relativeTime(fixture.startTime)}</span>
        </div>
      </header>

      {chance !== null && shownTeamA !== null ? (
        <div className="border-b border-line px-4 py-3">
          <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-2 text-xs">
            <span className="flex items-center gap-1.5 text-ink-faint">
              To win the series
              {drafted ? (
                <Detail
                  label="What 'draft in' means"
                  heading={
                    <span className="rounded bg-accent-bg px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-accent">
                      Draft in
                    </span>
                  }
                >
                  All ten heroes are locked and this number accounts for them. The bookmaker&rsquo;s
                  price below was set before anyone could see the draft.
                </Detail>
              ) : null}
            </span>
            <span className="tabular text-ink">
              {(chance * 100).toFixed(0)}% {favourite}
            </span>
          </div>
          <div className="flex h-2 overflow-hidden rounded-full bg-surface-raised">
            <div className="bg-accent" style={{ width: `${chance * 100}%` }} />
          </div>
          {shift !== null && Math.abs(shift) >= 0.5 ? (
            <p className="mt-1.5 text-xs text-ink-faint">
              The draft moved this {Math.abs(shift).toFixed(1)} points{' '}
              {shift > 0 ? 'towards' : 'away from'} {fixture.teamAName}, from{' '}
              <span className="tabular">{((before ?? 0) * 100).toFixed(0)}%</span>. The price below
              was set before the heroes were known.
            </p>
          ) : null}
        </div>
      ) : (
        <p className="border-b border-line px-4 py-3 text-xs text-ink-faint">
          No prediction — we could not work out who is likely to be playing for one of these teams.
        </p>
      )}

      {breakEven.available && breakEven.map ? (
        <div className="px-4 py-3">
          <div className="mb-2 flex items-baseline justify-between">
            <h4 className="text-xs font-medium text-ink-soft">
              {/*
                A Detail, not a Term, and the distinction is the one CLAUDE.md draws: a Term defines
                a word, a Detail derives a number. This heading sits over four prices computed from
                THIS fixture's probability, so a general definition of "break-even" left the reader
                to do the arithmetic themselves against figures the card never showed. It now works
                the sum through with the numbers they can see.

                The "+3 pts" badge that used to sit here is gone. It was the threshold expressed in
                units nothing else on the page uses, next to a column of decimal odds — so it read
                as three of something, and the something was never named.
              */}
              <BackingThreshold
                teamA={fixture.teamAName}
                mapProbability={mapForA}
                seriesProbability={breakEven.series ? seriesForA : null}
                minimumEdge={breakEven.minimumEdge ?? 0}
                map={breakEven.map.teamA}
                series={breakEven.series?.teamA ?? null}
              />
            </h4>
            {/* The series column is missing on a Bo1 rather than duplicated: there, the series and
                the map are the same bet, and two identical columns imply a distinction. */}
            <div className="tabular flex gap-4 text-xs uppercase tracking-wide text-ink-faint">
              {breakEven.series ? (
                <span className="w-16 text-right">
                  <Term id="series-market">Series</Term>
                </span>
              ) : null}
              <span className="w-16 text-right">
                <Term id="map-market">Map</Term>
              </span>
            </div>
          </div>

          {/*
            The estimate the thresholds came from, printed beside them.

            The card showed a MAP column of break-even prices and never once stated the map
            probability that produced it — `mapProbabilityTeamA` sits in the payload, unused. A
            reader comparing a map price had the threshold and not the number behind it, which is
            the one comparison the glossary calls the more honest of the two.
          */}
          {mapForA !== null ? (
            <p className="mb-2 text-xs text-ink-faint">
              We predict{' '}
              <span className="tabular text-ink-soft">
                {(seriesForA * 100).toFixed(0)}%
              </span>{' '}
              for {fixture.teamAName} over the series,{' '}
              <span className="tabular text-ink-soft">{(mapForA * 100).toFixed(0)}%</span> on a
              single map.
            </p>
          ) : null}

          <PriceRow
            team={fixture.teamAName}
            series={breakEven.series?.teamA}
            map={breakEven.map.teamA}
            hasSeries={Boolean(breakEven.series)}
          />
          <PriceRow
            team={fixture.teamBName}
            series={breakEven.series?.teamB}
            map={breakEven.map.teamB}
            hasSeries={Boolean(breakEven.series)}
          />

        </div>
      ) : null}

      <Offers fixture={fixture} />
      <WhyPanel fixtureId={fixture.id} teamA={fixture.teamAName} teamB={fixture.teamBName} />
      <RosterNote confidence={fixture.rosterConfidence} />
    </article>
  )
}

/**
 * What the bookmaker is actually offering, beside what we think it is worth.
 *
 * This is where the site earns its keep. A threshold on its own asks the reader to go and look the
 * price up; putting the two together turns the whole card into one comparison they can act on or
 * dismiss in a second.
 *
 * The edge is computed server-side, because it depends on which market the price belongs to — a
 * series price must be compared against the model's map estimate put through the series conversion,
 * never against the map estimate itself. That is not a decision to leave to a display component.
 */
function Offers({ fixture }: { fixture: Fixture }) {
  // The bar, as the server applied it. Read rather than assumed: a component that knew the number
  // would be a second copy of the betting rule.
  const threshold = fixture.breakEven.minimumEdge ?? null

  /*
   * NOT PRICED is a different answer from NO EDGE, and the card used to give neither.
   *
   * With no captured market this block simply did not render, so the one card on the site whose job
   * is to compare our number against the market showed only our number and said nothing about the
   * other half. Silence reads as "we looked and there is nothing here". What it actually means is
   * that no price has been bought yet — and that is the more interesting state, because a price
   * exists only in the minutes before kick-off, cannot be re-fetched afterwards, and is rationed
   * against a monthly budget.
   */
  if (!fixture.markets || fixture.markets.length === 0) {
    return (
      <p className="border-t border-line px-4 py-2 text-xs text-ink-faint">
        <Detail
          label="Why there is no price yet"
          className="text-ink-soft"
          heading={<span>Price pending</span>}
        >
          We buy a price when the lobby opens and the market is live, and again within five minutes
          of the draft settling — the window this site exists to catch. If no lobby is seen we fall
          back to twenty minutes before the scheduled start.
          <span className="mt-2 block border-t border-line pt-2 text-ink-faint">
            A line is withdrawn at the horn and archived nowhere, so there is one chance per fixture
            and it is rationed against a monthly budget. Some matches are never priced by any book.
          </span>
        </Detail>
      </p>
    )
  }

  /*
   * One book, and its markets — series first, because that is the market most people bet and the one
   * our headline number describes.
   *
   * `fixture.markets` carries a row per (bookmaker, market), and this block used to render all of
   * them under a heading naming only the first. On a fixture three books had priced, the card showed
   * "SERIES" three times and "MAP 1" three times, with different numbers, no book named against any
   * of them, and a heading claiming the lot was Pinnacle. The React keys collided too — three rows
   * keyed "series".
   *
   * The card is a summary; the bet record is where books are compared, and it has a picker for it.
   * So the card takes the best-ranked book the server sends and says whose prices these are.
   */
  const book = fixture.markets[0]?.bookmaker

  const ordered = [...fixture.markets]
    .filter((quote) => quote.bookmaker === book)
    .sort((a, b) => (a.market === 'series' ? -1 : b.market === 'series' ? 1 : 0))

  return (
    <div className="border-t border-line px-4 py-3">
      <div className="mb-2 flex items-baseline justify-between">
        <h4 className="text-xs font-medium text-ink-soft">
          On offer at {ordered[0].bookmakerName}
        </h4>
        <span className="text-xs text-ink-faint">
          {((ordered[0].overround - 1) * 100).toFixed(1)}% <Term id="margin">margin</Term>
        </span>
      </div>

      {ordered.map((quote) => (
        <div key={quote.market} className="mb-1.5 last:mb-0">
          <div className="mb-1 flex items-baseline gap-2 text-xs uppercase tracking-wide text-ink-faint">
            {/*
              One vocabulary, from `markets.ts`. This was a fourth copy of it, and the only one that
              could not say "Map 2" — every market that was not literally `map1` rendered under the
              heading "Series", so a spread or a kill handicap arrived labelled as the series
              market. The server no longer sends those; naming them properly is what stops the next
              one being mislabelled rather than merely absent.
            */}
            <span>{marketLabel(quote.market)}</span>
            {/*
              The verdict, while a bet is still possible.

              It used to appear only on a card whose match had already started — so the word arrived
              after the market closed and was withheld at the only moment anyone could act on it. A
              reader had to know the threshold and apply it to two edges themselves.

              The server decides; this renders. A component that re-derived the threshold would be a
              second copy of the betting rule, which is how it was before.
            */}
            {quote.verdict?.recommended ? (
              <span className="rounded bg-accent-bg px-1.5 py-0.5 normal-case tracking-normal text-accent">
                advised:{' '}
                {quote.verdict.side === 'A' ? quote.teamA.team : quote.teamB.team}
              </span>
            ) : quote.verdict ? (
              <span className="normal-case tracking-normal text-ink-faint">no edge</span>
            ) : null}
          </div>
          {[quote.teamA, quote.teamB].map((side) => (
            <div
              key={side.team}
              className="flex items-baseline justify-between gap-2 py-0.5 text-sm"
            >
              <span className="min-w-0 truncate text-ink-soft">{side.team}</span>
              <span className="tabular flex shrink-0 items-baseline gap-3">
                {/*
                  Drift since the first price we saw. Lengthening odds mean the market moved
                  against this side after we started watching.

                  It sits to the LEFT of the price, and that is the whole reason it is placed
                  rather than merely present: we have no opening price for every market, so between
                  the price and the edge it pushed both columns sideways on the markets that had one
                  and left them where they were on the markets that did not — the series, map 1 and
                  map 2 rows of one card no longer lined up. Everything to the right of the price is
                  fixed-width, so growing leftwards into the space beside the team name is the one
                  direction an optional column can grow without moving anything.
                */}
                {quote.movement && Math.abs(side.odds - side.openingOdds) >= 0.005 ? (
                  <Detail
                    label={`Line movement for ${side.team}`}
                    className="w-12 text-right text-xs text-ink-faint"
                    heading={
                      <span>
                        {side.odds > side.openingOdds ? '↑' : '↓'}
                        {Math.abs(side.odds - side.openingOdds).toFixed(2)}
                      </span>
                    }
                  >
                    Opened at{' '}
                    <span className="tabular text-ink">{side.openingOdds.toFixed(2)}</span>, now{' '}
                    <span className="tabular text-ink">{side.odds.toFixed(2)}</span>.{' '}
                    {side.odds > side.openingOdds
                      ? 'Lengthening: the market moved against this side after we started watching.'
                      : 'Shortening: money has come for this side since we started watching.'}
                  </Detail>
                ) : null}
                <span className="text-ink">{side.odds.toFixed(2)}</span>
                {side.edge === null ? null : (
                  <Detail
                    label={`Edge on ${side.team}`}
                    // Colour follows the server's verdict rather than a threshold repeated
                    // here. This read `side.edge >= 0.03`, which was the betting rule living in a
                    // component and free to drift from the one that decides bets.
                    className={`w-14 text-right text-xs ${
                      quote.verdict?.recommended
                      && (quote.verdict.side === 'A') === (side === quote.teamA)
                        ? 'text-win'
                        : 'text-ink-faint'
                    }`}
                    heading={
                      <span>
                        {side.edge >= 0 ? '+' : ''}
                        {(side.edge * 100).toFixed(1)}
                      </span>
                    }
                  >
                    We make this side{' '}
                    <span className="tabular text-ink">
                      {((side.impliedProbability + side.edge) * 100).toFixed(0)}%
                    </span>{' '}
                    against the{' '}
                    <span className="tabular text-ink">
                      {(side.impliedProbability * 100).toFixed(0)}%
                    </span>{' '}
                    that {side.odds.toFixed(2)} implies.{' '}
                    {/*
                      Against the PRICE, margin and all, because the price is what a stake is paid
                      at. This used to print the gap to the de-vigged line, which is a different and
                      larger number — 4.6 points beside a 1.31 the card said needed 1.36. Both are
                      real quantities; only this one is the bet.

                      The threshold is not restated here. It is the same comparison as the "worth
                      backing at" price above, and the verdict beside the market name is the server's
                      answer to it — a sentence repeating the rule would be a third copy of it.
                    */}
                    {threshold === null
                      ? null
                      : side.edge >= threshold
                        ? 'That clears the bar, so it is worth backing.'
                        : `Under the bar, which needs ${(threshold * 100).toFixed(0)} points.`}
                  </Detail>
                )}
              </span>
            </div>
          ))}
        </div>
      ))}

    </div>
  )
}

function PriceRow({
  team,
  series,
  map,
  hasSeries,
}: {
  team: string
  series: BreakEvenSide | undefined
  map: BreakEvenSide
  hasSeries: boolean
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-t border-line/60 py-1.5 text-sm first:border-t-0">
      <span className="min-w-0 truncate text-ink-soft">{team}</span>
      <span className="tabular flex shrink-0 gap-4 text-ink">
        {hasSeries ? <Price side={series} /> : null}
        <Price side={map} />
      </span>
    </div>
  )
}

/**
 * One threshold.
 *
 * A null price means no odds on this side clear the edge — which happens when we make a team so
 * unlikely that even a generous price is bad value. Shown as a dash rather than omitted, because a
 * blank cell reads as missing data instead of as an answer.
 */
function Price({ side }: { side: BreakEvenSide | undefined }) {
  if (!side || side.valueOdds === null) {
    return (
      <Detail
        label="Why there is no price here"
        className="w-16 text-right text-ink-faint"
        heading={<span>—</span>}
      >
        We make this side so unlikely that no realistic price would clear our minimum edge. A dash is
        the answer, not missing data.
      </Detail>
    )
  }

  return (
    <Detail
      label={`How ${side.valueOdds.toFixed(2)} was worked out`}
      className="w-16 text-right"
      heading={<span>{side.valueOdds.toFixed(2)}+</span>}
    >
      We make this side{' '}
      <span className="tabular text-ink">{(side.probability * 100).toFixed(0)}%</span>, so the fair
      price is <span className="tabular text-ink">{side.fairOdds?.toFixed(2)}</span> — zero
      expected profit. Anything from{' '}
      <span className="tabular text-ink">{side.valueOdds.toFixed(2)}</span> up pays enough above that
      to be worth taking.
    </Detail>
  )
}

/**
 * How much the lineup behind the number is an assumption.
 *
 * Only shown when it is worth qualifying. A "high" confidence lineup — the same five who played
 * together this week — is the normal case, and a badge on every card would train the reader to
 * ignore the one that matters.
 */
function RosterNote({ confidence }: { confidence: Fixture['rosterConfidence'] }) {
  if (confidence === null || confidence === 'high') {
    return null
  }

  const text =
    confidence === 'medium'
      ? 'Based on a lineup last seen a while ago — a roster change since would not be reflected.'
      : confidence === 'low'
        ? 'Based on each team’s declared roster rather than players we have seen play together. Treat as a rough guide.'
        : 'We could not establish a lineup for one of these teams.'

  return (
    <p className="border-t border-line px-4 py-2 text-xs text-ink-faint">
      <span className="text-warn">Note</span> · {text}
    </p>
  )
}
